import { apiRequest } from './index';

/**
 * Fetch all time adjustments with pagination and filtering
 *
 * @param {Object} options Request options
 * @param {number} options.page Page number
 * @param {number} options.limit Items per page
 * @param {string} options.type Filter by type (extra, missed)
 * @param {number} options.agentId Filter by agent ID
 * @param {string} options.dateFrom Filter by start date
 * @param {string} options.dateTo Filter by end date
 * @returns {Promise<Object>} Time adjustments with pagination
 */
export const fetchTimeAdjustments = async (options = {}) => {
  try {
    const { page = 1, limit = 20, type, agentId, dateFrom, dateTo } = options;

    // Build query parameters
    const params = [`page=${page}`, `limit=${limit}`];

    if (type) params.push(`type=${type}`);
    if (agentId) params.push(`agent_id=${agentId}`);
    if (dateFrom) params.push(`date_from=${encodeURIComponent(dateFrom)}`);
    if (dateTo) params.push(`date_to=${encodeURIComponent(dateTo)}`);

    const queryString = params.join('&');

    const response = await apiRequest({
      url: `/api/time_adjustments.php?method=get_all&${queryString}`,
      method: 'GET'
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching time adjustments:', error);
    throw error;
  }
};

/**
 * Fetch a single time adjustment
 *
 * @param {number} id Time adjustment ID
 * @returns {Promise<Object>} Time adjustment data
 */
export const fetchTimeAdjustment = async (id) => {
  try {
    const response = await apiRequest({
      url: `/api/time_adjustments.php?method=get&id=${id}`,
      method: 'GET'
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching time adjustment with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Add a new time adjustment
 *
 * @param {Object} data Time adjustment data
 * @returns {Promise<Object>} Result with new adjustment ID
 */
export const addTimeAdjustment = async (data) => {
  try {
    const response = await apiRequest({
      url: '/api/time_adjustments.php?method=add',
      method: 'POST',
      data
    });

    return response.data;
  } catch (error) {
    console.error('Error adding time adjustment:', error);
    throw error;
  }
};

/**
 * Update an existing time adjustment
 *
 * @param {Object} data Time adjustment data with ID
 * @returns {Promise<Object>} Result
 */
export const updateTimeAdjustment = async (data) => {
  try {
    const response = await apiRequest({
      url: `/api/time_adjustments.php?method=edit&id=${data.id}`,
      method: 'POST',
      data
    });

    return response.data;
  } catch (error) {
    console.error(`Error updating time adjustment with ID ${data.id}:`, error);
    throw error;
  }
};

/**
 * Delete a time adjustment
 *
 * @param {number} id Time adjustment ID
 * @returns {Promise<Object>} Result
 */
export const deleteTimeAdjustment = async (id) => {
  try {
    const response = await apiRequest({
      url: `/api/time_adjustments.php?method=delete&id=${id}`,
      method: 'POST'
    });

    return response.data;
  } catch (error) {
    console.error(`Error deleting time adjustment with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch time adjustments for a specific agent
 *
 * @param {number} agentId Agent ID
 * @param {string} dateFrom Start date filter (optional)
 * @param {string} dateTo End date filter (optional)
 * @returns {Promise<Array>} Time adjustments
 */
export const fetchTimeAdjustmentsByAgent = async (agentId, dateFrom = null, dateTo = null) => {
  try {
    let url = `/api/time_adjustments.php?method=get_by_agent&agent_id=${agentId}`;

    if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
    if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;

    const response = await apiRequest({
      url,
      method: 'GET'
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching time adjustments for agent ID ${agentId}:`, error);
    throw error;
  }
};
